import alterpressApi from './base';
import {
    IActivation,
    IActivationClubsPopulation,
    IActivationMutation,
    IActivationPlaceholder,
    IActivationPrices,
    IActivationTaskData,
    IOtherActivations,
} from '../../models/activation.model';
import { IActivationSeedingVerificationValues } from '../../formik/activation-seeding-verification.form';
import { IActivationPoNumberValues } from '../../formik/activation-po-number.form';
import { IOption } from '../../models/option.model';

export const activationApi = alterpressApi
    .enhanceEndpoints({
        addTagTypes: ['ACTIVATION', 'REACTIVATIONS'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getActivation: builder.query<IActivation, string | number>({
                query: (id) => `/activations/${id}?expand=media,prices,verifiedPrices`,
                providesTags: ['ACTIVATION'],
            }),
            getActivationClubs: builder.query<IActivationClubsPopulation, string[]>({
                query: (selectedClubIds) => ({
                    url: `/activations/clubs`,
                    params: selectedClubIds.length ? { selected: selectedClubIds.join(',') } : {},
                }),
            }),
            getReactivations: builder.query<IOption[], undefined>({
                query: () => `/activations/available-to-reactivate`,
                providesTags: ['REACTIVATIONS'],
            }),
            saveActivation: builder.mutation<IActivation, IActivationMutation>({
                query: (data) => ({
                    method: 'POST',
                    url: `/activations`,
                    body: data,
                }),
            }),
            updateActivation: builder.mutation<IActivation, { id: string | number }>({
                query: ({ id, ...data }) => ({
                    method: 'PATCH',
                    url: `/activations/${id}`,
                    body: data,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            deleteActivation: builder.mutation<undefined, string | number>({
                query: (id) => ({
                    method: 'DELETE',
                    url: `/activations/${id}`,
                }),
            }),
            calculateActivation: builder.mutation<IActivationPrices, IActivationMutation>({
                query: (data) => ({
                    method: 'POST',
                    url: `/activations/calculate`,
                    body: data,
                }),
            }),
            calculateVerifiedActivation: builder.mutation<
                IActivationPrices,
                { verifiedPackagesCount: number; activationId: number }
            >({
                query: ({ activationId, verifiedPackagesCount }) => ({
                    method: 'POST',
                    url: `/activations/${activationId}/calculate-verified`,
                    body: { verifiedPackagesCount },
                }),
            }),
            verifyActivation: builder.mutation<
                void,
                { values: IActivationSeedingVerificationValues; activationId: number }
            >({
                query: ({ activationId, values }) => ({
                    method: 'POST',
                    url: `/activations/${activationId}/verify`,
                    body: values,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            unverifyActivation: builder.mutation<void, number>({
                query: (activationId) => ({
                    method: 'POST',
                    url: `/activations/${activationId}/unverify`,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            otherActivations: builder.mutation<
                IOtherActivations[],
                { tasks: IActivationTaskData[] }
            >({
                query: (data) => ({
                    method: 'POST',
                    url: `/activations/check-periods`,
                    body: data,
                }),
            }),
            updatePoNumber: builder.mutation<
                void,
                { values: IActivationPoNumberValues; activationId: number }
            >({
                query: ({ activationId, values }) => ({
                    method: 'PATCH',
                    url: `/activations/${activationId}/po-number`,
                    body: values,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            sendIncomingDeadlineEmail: builder.mutation<void, { activationId: string | number }>({
                query: ({ activationId }) => ({
                    method: 'POST',
                    url: `/activations/${activationId}/send-copy-incoming-deadline-email`,
                }),
            }),
            sendPoReminderEmail: builder.mutation<void, { activationId: string | number }>({
                query: ({ activationId }) => ({
                    method: 'POST',
                    url: `/activations/${activationId}/send-po-reminder-email`,
                }),
            }),
            createPlaceholderActivation: builder.mutation<
                IActivationPlaceholder,
                IActivationPlaceholder
            >({
                query: (data) => ({
                    method: 'POST',
                    url: `/activations/placeholder`,
                    body: data,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            updatePlaceholderActivation: builder.mutation<
                IActivationPlaceholder,
                { id: string | number }
            >({
                query: ({ id, ...data }) => ({
                    method: 'PATCH',
                    url: `/activations/placeholder/${id}`,
                    body: data,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            deletePlaceholderActivation: builder.mutation<void, { id: string | number }>({
                query: ({ id }) => ({
                    method: 'DELETE',
                    url: `/activations/placeholder/${id}`,
                }),
                invalidatesTags: ['ACTIVATION'],
            }),
            holdActivationOneTribe: builder.mutation<
                void,
                { id: string | number; message: string }
            >({
                query: ({ id, message }) => ({
                    method: 'POST',
                    url: `/activations/${id}/hold-activation/one2tribe`,
                    body: message,
                }),
            }),
            holdActivationOwner: builder.mutation<void, { id: string | number; message: string }>({
                query: ({ id, message }) => ({
                    method: 'POST',
                    url: `/activations/${id}/hold-activation/owner`,
                    body: { message: message },
                }),
            }),
            readyToGoLive: builder.mutation<void, { id: string | number }>({
                query: ({ id }) => ({
                    method: 'POST',
                    url: `/activations/${id}/ready-to-go-live`,
                }),
            }),
        }),
    });

export const {
    useGetActivationQuery,
    useGetActivationClubsQuery,
    useGetReactivationsQuery,
    useSaveActivationMutation,
    useUpdateActivationMutation,
    useDeleteActivationMutation,
    useCalculateActivationMutation,
    useOtherActivationsMutation,
    useCalculateVerifiedActivationMutation,
    useVerifyActivationMutation,
    useUnverifyActivationMutation,
    useUpdatePoNumberMutation,
    useSendIncomingDeadlineEmailMutation,
    useSendPoReminderEmailMutation,
    useCreatePlaceholderActivationMutation,
    useUpdatePlaceholderActivationMutation,
    useDeletePlaceholderActivationMutation,
    useHoldActivationOneTribeMutation,
    useHoldActivationOwnerMutation,
    useReadyToGoLiveMutation,
} = activationApi;
